import { ListRequest, ListRequestSchemaV2 } from "../types/common/commonTypes";
import { CompanyType } from "../types/companyTypes";
import { PersonType } from "../types/personTypes";
import { InvPosRequest } from "../types/invPosTypes";
import { DynamicRequestSchema } from "../types/dynamicQueryTypes";

/**
 * Query keys factory for consistent cache key management
 * This pattern ensures proper cache invalidation and prevents key conflicts
 */
export const queryKeys = {
  auth: {
    all: ["auth"] as const,
    status: () => [...queryKeys.auth.all, "status"] as const,
  },
  bots: {
    all: ["bots"] as const,
    lists: () => [...queryKeys.bots.all, "list"] as const,
  },
  mrp: {
    all: ["mrp"] as const,
    lists: () => [...queryKeys.mrp.all, "list"] as const,
    list: () => [...queryKeys.mrp.lists()] as const,
  },
  dynamic: {
    all: ["dynamic"] as const,
    lists: () => [...queryKeys.dynamic.all, "list"] as const,
    list: (params: DynamicRequestSchema) =>
      [...queryKeys.dynamic.lists(), params] as const,
  },
  audit: {
    all: ["audit"] as const,
    lists: () => [...queryKeys.audit.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.audit.lists(), params] as const,
    records: () => [...queryKeys.audit.all, "record"] as const,
    record: (table: string, id: string) =>
      [...queryKeys.audit.records(), table, id] as const,
    users: () => [...queryKeys.audit.all, "user"] as const,
    user: (id: string) => [...queryKeys.audit.users(), id] as const,
  },
  item: {
    all: ["item"] as const,
    listsv2: () => [...queryKeys.item.all, "listsv2"] as const,
    listv2: (params: ListRequestSchemaV2 | null, source: string) =>
      [...queryKeys.item.listsv2(), params, source] as const,
    lists: () => [...queryKeys.item.all, "list"] as const,
    list: (params: ListRequest) => [...queryKeys.item.lists(), params] as const,
    details: () => [...queryKeys.item.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.item.details(), id] as const,
    inventory: (id: string) =>
      [...queryKeys.item.all, "inventory", id] as const,
  },
  uomtype: {
    all: ["uomtype"] as const,
    lists: () => [...queryKeys.uomtype.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.uomtype.lists(), params] as const,
    details: () => [...queryKeys.uomtype.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.uomtype.details(), id] as const,
  },
  uomunit: {
    all: ["uomunit"] as const,
    lists: () => [...queryKeys.uomunit.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.uomunit.lists(), params] as const,
    details: () => [...queryKeys.uomunit.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.uomunit.details(), id] as const,
  },
  location: {
    all: ["location"] as const,
    lists: () => [...queryKeys.location.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.location.lists(), params] as const,
    details: () => [...queryKeys.location.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.location.details(), id] as const,
  },
  lot: {
    all: ["lot"] as const,
    listsv2: () => [...queryKeys.lot.all, "listsv2"] as const,
    listv2: (params: ListRequestSchemaV2 | null, source: string) =>
      [...queryKeys.lot.listsv2(), params, source] as const,
    lists: () => [...queryKeys.lot.all, "list"] as const,
    list: (params: ListRequest) => [...queryKeys.lot.lists(), params] as const,
    details: () => [...queryKeys.lot.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.lot.details(), id] as const,
  },
  invstatus: {
    all: ["invstatus"] as const,
    lists: () => [...queryKeys.invstatus.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.invstatus.lists(), params] as const,
    details: () => [...queryKeys.invstatus.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.invstatus.details(), id] as const,
  },
  invposition: {
    all: ["invposition"] as const,
    lists: () => [...queryKeys.invposition.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.invposition.lists(), params] as const,
    listsv2: () => [...queryKeys.invposition.all, "listv2"] as const,
    listv2: (request: InvPosRequest) =>
      [...queryKeys.invposition.listsv2(), request] as const,
    itemLists: () => [...queryKeys.invposition.all, "itemList"] as const,
    itemList: (itemId: string) =>
      [...queryKeys.invposition.itemLists(), itemId] as const,
    details: () => [...queryKeys.invposition.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.invposition.details(), id] as const,
  },
  invmovement: {
    all: ["invmovement"] as const,
    listsv2: () => [...queryKeys.invmovement.all, "listv2"] as const,
    listv2: (params: ListRequestSchemaV2 | null, source: string) =>
      [...queryKeys.invmovement.listsv2(), params, source] as const,
    lists: () => [...queryKeys.invmovement.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.invmovement.lists(), params] as const,
    details: () => [...queryKeys.invmovement.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.invmovement.details(), id] as const,
  },
  company: {
    all: ["company"] as const,
    lists: () => [...queryKeys.company.all, "list"] as const,
    list: (params: ListRequest, entity_type: CompanyType) =>
      [...queryKeys.company.lists(), params, entity_type] as const,
    details: () => [...queryKeys.company.all, "detail"] as const,
    detail: (id: string, type: string) =>
      [...queryKeys.company.details(), id, type] as const,
  },
  person: {
    all: ["person"] as const,
    lists: () => [...queryKeys.person.all, "list"] as const,
    list: (params: ListRequest, entity_type: PersonType) =>
      [...queryKeys.person.lists(), params, entity_type] as const,
    details: () => [...queryKeys.person.all, "detail"] as const,
    detail: (id: string, type: string) =>
      [...queryKeys.person.details(), id, type] as const,
  },
  address: {
    all: ["address"] as const,
    lists: () => [...queryKeys.address.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.address.lists(), params] as const,
    details: () => [...queryKeys.address.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.address.details(), id] as const,
  },
  shipment: {
    all: ["shipment"] as const,
    lists: () => [...queryKeys.shipment.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.shipment.lists(), params] as const,
    details: () => [...queryKeys.shipment.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.shipment.details(), id] as const,
  },
  user: {
    all: ["user"] as const,
    lists: () => [...queryKeys.user.all, "list"] as const,
    list: (params: ListRequest) => [...queryKeys.user.lists(), params] as const,
    details: () => [...queryKeys.user.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.user.details(), id] as const,
  },
  purchaseorder: {
    all: ["purchaseorder"] as const,
    listsv2: () => [...queryKeys.purchaseorder.all, "listv2"] as const,
    listv2: (params: ListRequestSchemaV2) =>
      [...queryKeys.purchaseorder.listsv2(), params] as const,
    lists: () => [...queryKeys.purchaseorder.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.purchaseorder.lists(), params] as const,
    details: () => [...queryKeys.purchaseorder.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.purchaseorder.details(), id] as const,
  },
  salesorder: {
    all: ["salesorder"] as const,
    listsv2: () => [...queryKeys.salesorder.all, "listv2"] as const,
    listv2: (params: ListRequestSchemaV2 | null, source: string) =>
      [...queryKeys.salesorder.listsv2(), params, source] as const,
    lists: () => [...queryKeys.salesorder.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.salesorder.lists(), params] as const,
    details: () => [...queryKeys.salesorder.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.salesorder.details(), id] as const,
    bypos: () => [...queryKeys.salesorder.all, "bypo"] as const,
    bypo: (po: string) => [...queryKeys.salesorder.bypos(), po] as const,
  },
  workorder: {
    all: ["workorder"] as const,
    listsv2: () => [...queryKeys.workorder.all, "listv2"] as const,
    listv2: (params: ListRequestSchemaV2 | null, source: string) =>
      [...queryKeys.workorder.listsv2(), params, source] as const,
    filteredLists: () => [...queryKeys.workorder.all, "filteredList"] as const,
    filteredList: (params: ListRequest) =>
      [...queryKeys.workorder.filteredLists(), params] as const,
    listViews: () => [...queryKeys.workorder.all, "listView"] as const,
    listView: () => [...queryKeys.workorder.listViews()] as const,
    details: () => [...queryKeys.workorder.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.workorder.details(), id] as const,
  },
  bomrev: {
    all: ["bomrev"] as const,
    listsv2: () => [...queryKeys.bomrev.all, "listv2"] as const,
    listv2: (params: ListRequestSchemaV2 | null, source: string) =>
      [...queryKeys.bomrev.listsv2(), params, source] as const,
    lists: () => [...queryKeys.bomrev.all, "list"] as const,
    list: (params: ListRequest) =>
      [...queryKeys.bomrev.lists(), params] as const,
    details: () => [...queryKeys.bomrev.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.bomrev.details(), id] as const,
  },
  bomcomp: {
    all: ["bomcomp"] as const,
    lists: () => [...queryKeys.bomcomp.all, "list"] as const,
    list: (params: ListRequestSchemaV2 | null, source: string) =>
      [...queryKeys.bomcomp.lists(), params, source] as const,
    details: () => [...queryKeys.bomcomp.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.bomcomp.details(), id] as const,
  },
  world: {
    all: ["world"] as const,
    countries: () => [...queryKeys.world.all, "country"] as const,
    country: (countryCode: string) =>
      [...queryKeys.world.countries(), countryCode] as const,
    states: () => [...queryKeys.world.all, "state"] as const,
    state: (stateCode: string) =>
      [...queryKeys.world.states(), stateCode] as const,
    cities: () => [...queryKeys.world.all, "city"] as const,
    city: (city: string) => [...queryKeys.world.cities(), city] as const,
  },
  tgcm: {
    all: ["tgcm"] as const,
    lists: () => [...queryKeys.tgcm.all, "list"] as const,
    list: (params: ListRequestSchemaV2 | null, source: string) =>
      [...queryKeys.tgcm.lists(), params, source] as const,
    details: () => [...queryKeys.tgcm.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.tgcm.details(), id] as const,
  },
  tgcmevent: {
    all: ["tgcmevent"] as const,
    lists: () => [...queryKeys.tgcmevent.all, "list"] as const,
    list: (params: ListRequestSchemaV2 | null, source: string) =>
      [...queryKeys.tgcmevent.lists(), params, source] as const,
    details: () => [...queryKeys.tgcmevent.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.tgcmevent.details(), id] as const,
  },
  complaint: {
    all: ["complaint"] as const,
    lists: () => [...queryKeys.complaint.all, "list"] as const,
    list: (params: DynamicRequestSchema) =>
      [...queryKeys.complaint.lists(), params] as const,
    details: () => [...queryKeys.complaint.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.complaint.details(), id] as const,
  },
  rma: {
    all: ["rma"] as const,
    lists: () => [...queryKeys.rma.all, "list"] as const,
    list: (params: DynamicRequestSchema) =>
      [...queryKeys.rma.lists(), params] as const,
    details: () => [...queryKeys.rma.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.rma.details(), id] as const,
  },
  issuetracking: {
    all: ["issuetracking"] as const,
    lists: () => [...queryKeys.issuetracking.all, "list"] as const,
    list: (params: DynamicRequestSchema) =>
      [...queryKeys.issuetracking.lists(), params] as const,
    details: () => [...queryKeys.issuetracking.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.issuetracking.details(), id] as const,
  },
  registration: {
    all: ["registration"] as const,
    tokens: () => [...queryKeys.registration.all, "token"] as const,
    token: (token: string) =>
      [...queryKeys.registration.tokens(), token] as const,
  },
  // Future: Add other entity query keys here
  // customers: { ... },
  // salesOrders: { ... },
  // etc.
};
